import {
  BarsOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  HomeOutlined,
  LeftCircleFilled,
  MenuOutlined,
  PoweroffOutlined,
  SettingOutlined,
  SolutionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { APP_PREFIX_PATH, AUTHENTICATED_ENTRY } from "configs/AppConfig";
const getIconForMenu = (menuName) => {
  switch (menuName) {
    case "DatabaseOutlined":
      return DatabaseOutlined;
    case "DashboardOutlined":
      return DashboardOutlined;
    case "UserOutlined":
      return UserOutlined;
    case "HomeOutlined":
      return HomeOutlined;
    case "PoweroffOutlined":
      return PoweroffOutlined;
    case "MenuOutlined":
      return MenuOutlined;
    case "BarsOutlined":
      return BarsOutlined;
    case "SettingOutlined":
      return SettingOutlined;
    default:
      return undefined;
  }
};
// These regime screens are now rendered as tabs inside the Electrical Regime screen
// (see views/app-views/ElectricalRegime/ElectricalRegime.js) instead of as their own
// sidebar entries — kept out of the menu here so they don't also appear standalone.
export const REGIME_PATHS_MERGED_INTO_ELECTRICAL_REGIME = [
  "InstrumentationControl",
  "PipingPressureSystems",
  "CivilStructural",
  "CleaningSanitationHousekeeping",
  "SafetyFireEnvironmentalHSE",
];
// All 6 regime roles render as tabs inside one shared page (see ElectricalRegime.js), so they
// collapse onto a single "Regimes" sidebar entry rather than one entry each.
const ALL_REGIME_PATHS = ["ElectricalRegime", ...REGIME_PATHS_MERGED_INTO_ELECTRICAL_REGIME];
const getChildRoles = (roleId, rolesList, parentKey = `r${roleId}`) => {
  const nav = [];
  const queue = [{ roleId, parentNav: nav, parentKey }];

  while (queue.length > 0) {
    const { roleId: currentRoleId, parentNav, parentKey: currentKey } = queue.shift();
    const siblingRoles = rolesList.filter((x) => x.parentId == currentRoleId);

    // Among this parent's children, any one of the 6 regime roles unlocks the single "Regimes"
    // entry — prefer the ElectricalRegime role's own title/icon when the user has it, otherwise
    // fall back to whichever regime role they do have, so access to just e.g.
    // PipingPressureSystems (without ElectricalRegime itself) still surfaces a way in. Without
    // this, a user granted only a merged regime role saw no sidebar entry at all.
    const regimeSiblings = siblingRoles.filter((x) => ALL_REGIME_PATHS.includes(x.path));
    const regimeMenuSource =
      regimeSiblings.find((x) => x.path === "ElectricalRegime") || regimeSiblings[0];

    const childRoles = [
      ...siblingRoles.filter((x) => !ALL_REGIME_PATHS.includes(x.path)),
      ...(regimeMenuSource ? [{ ...regimeMenuSource, path: "ElectricalRegime" }] : []),
    ];

    childRoles.forEach((x) => {
      // Stable keys from the role id chain (parent-child), so the menu can open and
      // highlight the current page (setDefaultOpen in MenuContent splits on "-").
      const item = {
        key: `${currentKey}-${x.roleId}`,
        path: `${APP_PREFIX_PATH}/${x.path}`,
        title: x.title,
        isGroupTitle: false,
        showItem: true,
        icon: getIconForMenu(x.icon),
        submenu: [],
        breadcrumb: true,
        isFolder: isFolder(x),
      };

      parentNav.push(item);

      if (!x.isLeaveNode) {
        queue.push({ roleId: x.roleId, parentNav: item.submenu, parentKey: item.key });
      }
    });
  }

  return nav;
};
//This function checks if roles or child roles have the provided path in children
const roleHasGivenScreens = (startId, rolesList_, path) => {
  let stack = [startId];
  let visited = new Set();
  while (stack.length > 0) {
    let currentId = stack.pop();
    // Skip already visited roles
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    // Find the role by ID
    let currentRole = rolesList_.find((role) => role.roleId === currentId);
    if (!currentRole) continue;
    // Check the path of the current role
    if (path.some((x) => x == currentRole.path)) {
      return true;
    }
    // Add child roles to the stack
    let childRoles = rolesList_.filter((role) => role.parentId === currentId);
    childRoles.forEach((child) => stack.push(child.roleId));
  }
  return false;
  //when no more childRoles output will be RoleId (integer);
  //when screen name's path find belongs to admin output will be true, bool;
};
//Order of Roles by Rizwan Qadri
const orderOfRoles = [
  { path: ["Home"], hasSeparator: false },
  { path: ["DashboardAnalytics"], hasSeparator: false },
  { path: ["MaintenanceDashboard"], hasSeparator: false },
  { path: ["TrackerDashboard"], hasSeparator: false },
  { path: ["LivePlant"], hasSeparator: false },
  { path: ["CommandDashboard"], hasSeparator: false },
  { path: ["Dashboard"], hasSeparator: false },
  { path: ["PreventiveMaintenanceDashboard"], hasSeparator: false },
  
  //{ path: ["AlertsDashboard"], hasSeparator: true },
  {
    path: [
      "MillPerformanceCorelation",
      "MillIdealCorelation",
      "PreparationPerformanceCorelation",
      "CPE_IdealCorelation",
      "MillCorelationReport",
      "PerformanceCorrelation",
      "CPECorelationReport",
      "PHPerformanceCorelation",
      "PowerHouseCorelationReport",
      "PowerHouseIdealCorrelation",
      "PowerHouseCorrelation",
      "PowerHousePerformanceComparison",
      "PowerHousePerformanceWorkspace",
      "BoilerPerformanceCorelation",
      "BoilerIdealCorelation",
      "BoilerCorelationReport",
      "BoilerPerformanceDetailedReport",
      "PerformanceCoRelation",
      "PerformanceCoRelationTargets",
    ],
    hasSeparator: false,
  },

  { path: ["Stoppages"], hasSeparator: false },
  { path: ["StopDayPlan"], hasSeparator: false },
  {
    path: [
      "DemandGenration",
      "DemandGenrationReport",
      "DemandAndSpend",
      "DemandGraphicalReport",
      "CompareDemandGenration",
  
      "PreventiveMaintenancePlanPerview",
      "PreventiveMaintenancePlanComplianceReport"
    ],
    hasSeparator: false,
  },
  {
    path: [
      "ApprovalHistory",
      "AssemblyWiseSpares",
      "SpareHealthDetailHistory",
      "HealthWiseSpare",
      "SpareHealthHistoryAverage",
      "AssemblySparesHealthGraphical",
      "AssemblySparesHealthGraphical",
      "LogReport",
      "DrawingHistoryReport",
      "AssemblyWiseCriticalSpares",
      "SpareCountWiseReport",
      "StockAndShortages",
      "HardwareCountReport",
      "AllSpareConsumptionReport",
      "WareHouseReport",
      "RepairCountReport",
      "DepartmentJobReport",
      "DepartmentJobReport",
      "JobGraphicalReport",
      "WeeklyDeptWorkOrderUtilizationReport",
      "WorkOrderPerformance",
      "LabourUtilisationReport",
      "StoppagesReport",
      "StoppagesReportByAssemblySpare",
      "StoppageAnalysis",
    ],
    hasSeparator: false,
  },
  {
    path: [
      "CHE_Assembly",
      "CHE_AssemblySubAssemblyMapping",
      "CHE_Spare",
      "CCE_Assembly",
      "CCE_AssemblySubAssemblyMapping",
      "CCE_Spare",
      "CPE_Assembly",
      "CPE_SubAssembly",
      "CPE_AssemblySubAssemblyMapping",
      "CPE_Spare",
      "PME_Assembly",
      "PME_AssemblySubAssemblyMapping",
      "PME_Spare",
      "EIE_Assembly",
      "EIE_AssemblySubAssemblyMapping",
      "EIE_Spare",
      "JHE_Assembly",
      "JHE_AssemblySubAssemblyMapping",
      "JHE_Spare",
      "OCE_Assembly",
      "OCE_AssemblySubAssemblyMapping",
      "OCE_Spare",
      "CME_Mill",
      "CME_SubAssembly",
      "CME_MillSubAssemblyMapping",
      "CME_Spare",
      "OTH_Assembly",
      "OTH_AssemblySubAssemblyMapping",
      "OTH_Spare",
    ],
    hasSeparator: true,
  },
  {
    path: [
      "JHCL_Assembly",
      "JHCL_AssemblySubAssemblyMapping",
      "JHCL_Spare",
      "LSCL_Assembly",
      "LSCL_AssemblySubAssemblyMapping",
      "LSCL_Spare",
      "DCL_Assembly",
      "DCL_AssemblySubAssemblyMapping",
      "DCL_Spare",
      "CCL_Assembly",
      "CCL_AssemblySubAssemblyMapping",
      "CCL_Spare",
      "FCL_Assembly",
      "FCL_AssemblySubAssemblyMapping",
      "FCL_Spare",
      "ECL_Assembly",
      "ECL_AssemblySubAssemblyMapping",
      "ECL_Spare",
      "CDCL_Assembly",
      "CDCL_AssemblySubAssemblyMapping",
      "CDCL_Spare",
      "STCL_Assembly",
      "STCL_AssemblySubAssemblyMapping",
      "STCL_Spare",
      "EECL_Assembly",
      "EECL_AssemblySubAssemblyMapping",
      "EECL_Spare",
      "ICL_Assembly",
      "ICL_AssemblySubAssemblyMapping",
      "ICL_Spare",
      "PCR_Assembly",
      "PCR_AssemblySubAssemblyMapping",
      "PCR_Spare",
      "CCR_Assembly",
      "CCR_AssemblySubAssemblyMapping",
      "CCR_Spare",
      "COCR_Assembly",
      "COCR_AssemblySubAssemblyMapping",
      "COCR_Spare",
      "STCR_Assembly",
      "STCR_AssemblySubAssemblyMapping",
      "STCR_Spare",
      "CTCR_Assembly",
      "CTCR_AssemblySubAssemblyMapping",
      "CTCR_Spare",
      "TRCR_Assembly",
      "TRCR_AssemblySubAssemblyMapping",
      "TRCR_Spare",
      "ISCR_Assembly",
      "ISCR_AssemblySubAssemblyMapping",
      "ISCR_Spare",
      "EECR_Assembly",
      "EECR_AssemblySubAssemblyMapping",
      "EECR_Spare",
      "ICR_Assembly",
      "ICR_AssemblySubAssemblyMapping",
      "ICR_Spare",
      "RCCT_Assembly",
      "RCCT_AssemblySubAssemblyMapping",
      "RCCT_Spare",
      "RFCT_Assembly",
      "RFCT_AssemblySubAssemblyMapping",
      "RFCT_Spare",
      "PCT_Assembly",
      "PCT_AssemblySubAssemblyMapping",
      "PCT_Spare",
      "SCCT_Assembly",
      "SCCT_AssemblySubAssemblyMapping",
      "SCCT_Spare",
      "VCCT_Assembly",
      "VCCT_AssemblySubAssemblyMapping",
      "VCCT_Spare",
      "MTCT_Assembly",
      "MTCT_AssemblySubAssemblyMapping",
      "MTCT_Spare",
      "MMCT_Assembly",
      "MMCT_AssemblySubAssemblyMapping",
      "MMCT_Spare",
      "RTCT_Assembly",
      "RTCT_AssemblySubAssemblyMapping",
      "RTCT_Spare",
      "RCT_Assembly",
      "RCT_AssemblySubAssemblyMapping",
      "RCT_Spare",
      "EECT_Assembly",
      "EECT_AssemblySubAssemblyMapping",
      "EECT_Spare",
      "ICCT_Assembly",
      "ICCT_AssemblySubAssemblyMapping",
      "ICCT_Spare",
      "HBA_Assembly",
      "HBA_AssemblySubAssemblyMapping",
      "HBA_Spare",
      "DBA_Assembly",
      "DBA_AssemblySubAssemblyMapping",
      "DBA_Spare",
      "RBBA_Assembly",
      "RBBA_AssemblySubAssemblyMapping",
      "RBBA_Spare",
      "DCBA_Assembly",
      "DCBA_AssemblySubAssemblyMapping",
      "DCBA_Spare",
      "SEBA_Assembly",
      "SEBA_AssemblySubAssemblyMapping",
      "SEBA_Spare",
      "GBA_Assembly",
      "GBA_AssemblySubAssemblyMapping",
      "GBA_Spare",
      "SBBA_Assembly",
      "SBBA_AssemblySubAssemblyMapping",
      "SBBA_Spare",
      "BCBA_Assembly",
      "BCBA_AssemblySubAssemblyMapping",
      "BCBA_Spare",
      "SSBA_Assembly",
      "SSBA_AssemblySubAssemblyMapping",
      "SSBA_Spare",
      "EEBA_Assembly",
      "EEBA_AssemblySubAssemblyMapping",
      "EEBA_Spare",
      "ICBA_Assembly",
      "ICBA_AssemblySubAssemblyMapping",
      "ICBA_Spare",
      "ProcessHouseIdealCorrelation",
      "ProcessHouseCorrelation",
      "ProcessHouseCorelationReport",
      "ProcessHousePerformanceComparison",
      "ProcessHousePerformanceWorkspace",
    ],
    hasSeparator: false,
  },
  {
    path: [
      "FBH_Assembly",
      "FBH_AssemblySubAssemblyMapping",
      "FBH_Spare",
      "VBBH_Assembly",
      "VBBH_AssemblySubAssemblyMapping",
      "VBBH_Spare",
      "AHBH_Assembly",
      "AHBH_AssemblySubAssemblyMapping",
      "AHBH_Spare",
      "EBH_Assembly",
      "EBH_AssemblySubAssemblyMapping",
      "EBH_Spare",
      "FABH_Assembly",
      "FABH_AssemblySubAssemblyMapping",
      "FABH_Spare",
      "CBH_Assembly",
      "CBH_AssemblySubAssemblyMapping",
      "CBH_Spare",
      "DSBH_Spare",
      "DSBH_Assembly",
      "DSBH_AssemblySubAssemblyMapping",
      "FEBH_Assembly",
      "FEBH_AssemblySubAssemblyMapping",
      "FEBH_Spare",
      "ETBH_Assembly",
      "ETBH_AssemblySubAssemblyMapping",
      "ETBH_Spare",
      "IBH_Assembly",
      "IBH_AssemblySubAssemblyMapping",
      "IBH_Spare",
      "FWBH_Assembly",
      "FWBH_AssemblySubAssemblyMapping",
      "FWBH_Spare",
      "SDBH_Assembly",
      "SDBH_AssemblySubAssemblyMapping",
      "SDBH_Spare",
      "ASBH_Assembly",
      "ASBH_AssemblySubAssemblyMapping",
      "ASBH_Spare",
      "WTBH_Assembly",
      "WTBH_AssemblySubAssemblyMapping",
      "WTBH_Spare",
      "SHBH_Assembly",
      "SHBH_AssemblySubAssemblyMapping",
      "SHBH_Spare",
      "VBH_Assembly",
      "VBH_AssemblySubAssemblyMapping",
      "VBH_Spare",
    ],
    hasSeparator: false,
  },
  {
    path: [
      "TPH_Assembly",
      "TPH_AssemblySubAssemblyMapping",
      "TPH_Spare",
      "GPH_Assembly",
      "GPH_AssemblySubAssemblyMapping",
      "GPH_Spare",
      "EEPH_Assembly",
      "EEPH_AssemblySubAssemblyMapping",
      "EEPH_Spare",
      "ICPH_Assembly",
      "ICPH_AssemblySubAssemblyMapping",
      "ICPH_Spare",
      "OHPH_Assembly",
      "OHPH_AssemblySubAssemblyMapping",
      "OHPH_Spare",
    ],
    hasSeparator: false,
  },
  {
    path: [
      "MSDP_Assembly",
      "MSDP_AssemblySubAssemblyMapping",
      "MSDP_Spare",
      "USDP_Assembly",
      "USDP_AssemblySubAssemblyMapping",
      "USDP_Spare",
      "DDP_Assembly",
      "DDP_AssemblySubAssemblyMapping",
      "DDP_Spare",
      "PDP_Assembly",
      "PDP_AssemblySubAssemblyMapping",
      "PDP_Spare",
      "FDP_Assembly",
      "FDP_AssemblySubAssemblyMapping",
      "FDP_Spare",
      "DIDP_Assembly",
      "DIDP_AssemblySubAssemblyMapping",
      "DIDP_Spare",
      "EPDP_Assembly",
      "EPDP_AssemblySubAssemblyMapping",
      "EPDP_Spare",
      "IDP_Assembly",
      "IDP_AssemblySubAssemblyMapping",
      "IDP_Spare",
      "BDB_Assembly",
      "BDB_AssemblySubAssemblyMapping",
      "BDB_Spare",
      "BFDB_Assembly",
      "BFDB_AssemblySubAssemblyMapping",
      "BFDB_Spare",
      "FDDB_Assembly",
      "FDDB_AssemblySubAssemblyMapping",
      "FDDB_Spare",
      "SDB_Assembly",
      "SDB_AssemblySubAssemblyMapping",
      "SDB_Spare",
      "EPDB_Assembly",
      "EPDB_AssemblySubAssemblyMapping",
      "EPDB_Spare",
      "IDB_Assembly",
      "IDB_AssemblySubAssemblyMapping",
      "IDB_Spare",
      "WTDB_Assembly",
      "WTDB_AssemblySubAssemblyMapping",
      "WTDB_Spare",
      "FPDB_Assembly",
      "FPDB_AssemblySubAssemblyMapping",
      "FPDB_Spare",
      "CPETP_Assembly",
      "CPETP_AssemblySubAssemblyMapping",
      "CPETP_Spare",
      "FSETP_Assembly",
      "FSETP_AssemblySubAssemblyMapping",
      "FSETP_Spare",
      "ADETP_Assembly",
      "ADETP_AssemblySubAssemblyMapping",
      "ADETP_Spare",
      "BRETP_Assembly",
      "BRETP_AssemblySubAssemblyMapping",
      "BRETP_Spare",
      "DOETP_Assembly",
      "DOETP_AssemblySubAssemblyMapping",
      "DOETP_Spare",
      "DistilleryCorrelation",
      "DistilleryCorelationReport",
      "WSFS_Assembly",
      "WSFS_AssemblySubAssemblyMapping",
      "WSFS_Spare",
      "ESD_Assembly",
      "ESD_AssemblySubAssemblyMapping",
      "ESD_Spare",
      "FPP_Assembly",
      "FPP_AssemblySubAssemblyMapping",
      "FPP_Spare",
      "YRP_Assembly",
      "YRP_AssemblySubAssemblyMapping",
      "YRP_Spare",
      "EVAP_Assembly",
      "EVAP_AssemblySubAssemblyMapping",
      "EVAP_Spare",
    ],
    hasSeparator: false,
  },
  { path: ["WarehouseSpares"], hasSeparator: false },
  { path: ["RequestNewJob"], hasSeparator: true },
   { path: ["Telemetry"], hasSeparator: false },
   { path: ["TelemetryLive"], hasSeparator: false },
   { path: ["TelemetrySensorMatrix"], hasSeparator: false },
   { path: ["TelemetryAnalytics"], hasSeparator: false },
  {
    // The other regime screens (InstrumentationControl, PipingPressureSystems,
    // CivilStructural, CleaningSanitationHousekeeping, SafetyFireEnvironmentalHSE) are
    // rendered as tabs inside Electrical Regime now — see
    // REGIME_PATHS_MERGED_INTO_ELECTRICAL_REGIME above — so they're no longer separate
    // sidebar entries and don't need a sort-order slot here.
    path: ["ElectricalRegime"],
    hasSeparator: true,
  },
  { path: ["RegimeDashboard"], hasSeparator: false },

  {
    path: [
      "Category",
      "UserList",
      "Company",
      "Roles",
      "Permission",
      "ClientRegistration",
      "ApprovalHierarchy",
      "HouseImageMapping",
      "CrushingInfo",
      "ImageGallery",
      "Boilers",
      "DistilleryBoilers",
      "AlertNotifications",
    ],
    hasSeparator: true,
  },
  { path: ["ConfigureFactoryPreRequisite"], hasSeparator: false },
];
const sortInRQOrder = (navConfig, rolesList_) => {
  let navSorted = [];
  let remaining = [];
  const matchedRoleIds = new Set();
  orderOfRoles.forEach((r) => {
    for (let x of navConfig) {
      if (!matchedRoleIds.has(x.roleId) && roleHasGivenScreens(x.roleId, rolesList_, r.path)) {
        navSorted.push({ ...x, hasSeparator: r.hasSeparator });
        matchedRoleIds.add(x.roleId);
        break;
      }
    }
  });
  for (let x of navConfig) {
    if (!matchedRoleIds.has(x.roleId)) {
      remaining.push(x);
    }
  }
  if (navSorted.length >= 2) {
    const lastTwo = navSorted.splice(-2);
    navSorted = [...navSorted, ...remaining, ...lastTwo];
  } else {
    navSorted = [...navSorted, ...remaining];
  }

  return navSorted;
};
// One menu entry per screen (QA UI-06). Some factories hold two copies of most of their
// RolesNew rows (cloned twice, with a different tree for the reports), so the super admin
// menu showed most screens twice. Within the selected company and factory:
//   - a screen (a row with a real path) is kept once, the copy with the lowest roleId;
//   - a folder ("isParent" row) is merged with the folder of the same title under the same
//     parent; children of a dropped copy move to the kept one;
//   - a folder whose children were all duplicates (the old report tree) is not shown.
// factoryRoles itself is untouched: screens still look roles up by any roleId.
const normTitle = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9\u0E00-\u0E7F]/g, "");
const isFolder = (x) => !x.path || x.path === "isParent";
export const dedupeMenuRoles = (rolesList_) => {
  const roles = rolesList_ || [];
  const byId = new Map(roles.map((x) => [x.roleId, x]));
  const keys = new Map();
  const keyOf = (x, depth = 0) => {
    if (keys.has(x.roleId)) return keys.get(x.roleId);
    let key;
    if (!isFolder(x)) key = `screen:${x.path}`;
    else {
      const parent = Number(x.parentId) !== 0 ? byId.get(x.parentId) : null;
      key = `${parent && depth < 30 ? keyOf(parent, depth + 1) : ""}>${normTitle(x.title)}`;
    }
    keys.set(x.roleId, key);
    return key;
  };
  const keep = new Map(); // key -> kept roleId
  [...roles]
    .sort((a, b) => Number(a.roleId) - Number(b.roleId))
    .forEach((x) => {
      const k = keyOf(x);
      if (!keep.has(k)) keep.set(k, x.roleId);
    });
  const alias = (id) => {
    const x = byId.get(id);
    return x ? keep.get(keyOf(x)) : id;
  };
  let out = roles
    .filter((x) => keep.get(keyOf(x)) === x.roleId)
    .map((x) => (Number(x.parentId) !== 0 && alias(x.parentId) !== x.parentId ? { ...x, parentId: alias(x.parentId) } : x));
  // Drop folders emptied by the merge (repeat: a folder may only hold such folders). Folders
  // that had no children to begin with stay as they were.
  const hadChild = new Set(roles.map((x) => x.parentId));
  for (let pass = 0; pass < 10; pass++) {
    const hasChild = new Set(out.map((x) => x.parentId));
    const next = out.filter(
      (x) => !(isFolder(x) && !x.isLeaveNode && hadChild.has(x.roleId) && !hasChild.has(x.roleId)),
    );
    if (next.length === out.length) break;
    out = next;
  }
  return out;
};
// A folder ("isParent" row) opens no screen: its link leads to "Page not found" (QA V5-02).
// A folder whose children are all hidden from this user, or that has none, is left out of the
// menu and the search; its parent goes too when that leaves it empty.
export const dropEmptyFolders = (items) =>
  (items || [])
    .map((item) => (item.submenu?.length ? { ...item, submenu: dropEmptyFolders(item.submenu) } : item))
    .filter((item) => !(item.isFolder && !item.submenu?.length));
export const GetNavigationConfig = (rolesList_, userEmail = null) => {
  rolesList_ = dedupeMenuRoles(rolesList_);
  let navigationConfig = [];
  let navigationConfig_ = [];
  let parentRoles = [];
  const environment = process.env.NODE_ENV;

  //Commenting as per CEO Instruction when he was in ZIMBABWE.
  // if (userEmail) {
  //   const hasHomeRole = rolesList_.some((x) => x.path === "Home");
  //   if (
  //     hasHomeRole &&
  //     userEmail !== "super@admin.com" &&
  //     environment !== "development"
  //   ) {
  //     parentRoles = rolesList_.filter(
  //       (x) => x.isHome == false && x.parentId == 0
  //     );
  //   } else {
  //     parentRoles = rolesList_.filter((x) => x.parentId == 0);
  //   }
  // } else {
  //   parentRoles = rolesList_.filter((x) => x.parentId == 0);
  // }

  parentRoles = rolesList_.filter(
    (x) =>
      x.parentId == 0 &&
      x.isMenuPart == true &&
      !REGIME_PATHS_MERGED_INTO_ELECTRICAL_REGIME.includes(x.path),
  );
  parentRoles.forEach((x) => {
    const childRoles = x.isLeaveNode ? [] : getChildRoles(x.roleId, rolesList_);
    var item = {
      roleId: x.roleId,
      key: `r${x.roleId}`,
      path: `${APP_PREFIX_PATH}/${x.path}`,
      title: x.title,
      isGroupTitle: false,
      breadcrumb: true,
      showItem: true,
      icon: getIconForMenu(x.icon),
      submenu: childRoles,
      isFolder: isFolder(x),
    };
    navigationConfig_.push(item);
  });

  navigationConfig = sortInRQOrder(dropEmptyFolders(navigationConfig_), rolesList_);
  navigationConfig.push({
    roleId: Date.now(),
    key: "welcome-page",
    path: AUTHENTICATED_ENTRY,
    title: "Welcome",
    isGroupTitle: false,
    breadcrumb: true,
    showItem: false,
    submenu: [],
  });
  return navigationConfig;
};
